import { hashEconomicModelInput } from '../../lib/economicContribution/modelUtils';
import type {
  EconomicSourceCatalogEntry,
  EconomicSourceCatalogSnapshot,
  EconomicSourceRef,
} from '../../types/economicContribution';

export const ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID =
  'economic-source-semantic-audit-v1' as const;

export type EconomicSemanticUse =
  | 'operations-coverage-floor'
  | 'operations-fte-per-mw'
  | 'construction-peak-workers-per-mw'
  | 'construction-average-to-peak-ratio'
  | 'construction-duration-years';

export type EconomicSemanticFindingStatus =
  | 'confirmed'
  | 'confirmed-with-limitation'
  | 'not-applicable'
  | 'not-established';

interface EconomicSemanticFinding {
  status: EconomicSemanticFindingStatus;
  conclusion: string;
  locator: string;
}

interface EconomicSemanticClaimAssessment {
  use: EconomicSemanticUse;
  status: Extract<
    EconomicSemanticFindingStatus,
    'confirmed' | 'confirmed-with-limitation' | 'not-established'
  >;
  conclusion: string;
}

interface EconomicSemanticSupportingContext {
  title: string;
  publisher: string;
  url: string;
  excerpt: string;
  locator: string;
}

export interface EconomicSourceSemanticAuditRecord {
  sourceId: string;
  /** Hash of every catalog field whose meaning was assessed. */
  sourceFingerprint: string;
  policyId: typeof ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID;
  assessedAt: string;
  assessedBy: string;
  method: 'source-context-review';
  disposition: 'release-eligible' | 'withhold';
  artifactSha256?: string;
  excerpt: EconomicSemanticFinding;
  denominator: EconomicSemanticFinding;
  workforceDefinition: EconomicSemanticFinding;
  projectPhase: EconomicSemanticFinding;
  vintage: EconomicSemanticFinding;
  claims: readonly EconomicSemanticClaimAssessment[];
  supportingContext?: readonly EconomicSemanticSupportingContext[];
  limitations: readonly string[];
}

/**
 * Fingerprint the exact catalog representation that received the semantic
 * review. Any change to the quote, locator, units context, or normalized check
 * invalidates the audit and fails the runtime catalog closed until reassessed.
 */
export function economicSourceSemanticFingerprint(
  source: EconomicSourceRef,
): string {
  return hashEconomicModelInput({
    id: source.id,
    title: source.title,
    publisher: source.publisher,
    url: source.url,
    publicationDate: source.publicationDate ?? null,
    accessedAt: source.accessedAt,
    excerpt: source.excerpt,
    locator: source.locator,
    excerptKind: source.excerptKind,
    evidenceRole: source.evidenceRole,
    normalizedChecks: source.normalizedChecks ?? [],
    page: source.page ?? null,
    notes: source.notes ?? null,
  });
}

/**
 * Finite release-QA audit of the six documents that can drive model
 * coefficients. This records contextual interpretation; it does not claim
 * that code can independently prove a publication true.
 */
export const ECONOMIC_SOURCE_SEMANTIC_AUDITS: readonly EconomicSourceSemanticAuditRecord[] = [
  {
    sourceId: 'hamm-usc-data-center-employment-forecast-2025',
    sourceFingerprint: '8372f260c3a53e39c201aaa8b30ff79ff01b326bc6cf77d53c5884d2bc786ae3',
    policyId: ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID,
    assessedAt: '2026-08-27',
    assessedBy: 'codex-assisted-release-qa',
    method: 'source-context-review',
    disposition: 'release-eligible',
    excerpt: {
      status: 'confirmed',
      conclusion: 'The stored construction and operations intensity sentence appears verbatim.',
      locator: 'Executive summary, p. 1; detailed ranges, pp. 2 and 10',
    },
    denominator: {
      status: 'confirmed',
      conclusion: 'The report explicitly assigns size buckets and workforce intensities using total utility capacity, not critical IT MW.',
      locator: 'Methods, pp. 2-3; Appendix A2, p. 10',
    },
    workforceDefinition: {
      status: 'confirmed',
      conclusion: 'The baseline model defines direct employment as onsite peak construction headcount and operational FTE.',
      locator: 'Benchmark Summary, p. 1',
    },
    projectPhase: {
      status: 'confirmed-with-limitation',
      conclusion: 'The coefficients are national screening benchmarks assembled from operating and planned US hyperscale and colocation facilities, not verified outcomes for one project phase.',
      locator: 'Introduction, pp. 1-2; Appendix A1, pp. 8-9',
    },
    vintage: {
      status: 'confirmed',
      conclusion: 'Published in 2025 and described as using 2024-2025 field benchmarks.',
      locator: 'Title and introduction, p. 1',
    },
    claims: [
      {
        use: 'operations-fte-per-mw',
        status: 'confirmed-with-limitation',
        conclusion: 'The source supports direct onsite operations FTE per total utility MW, while acknowledging mixed disclosure quality and some analyst-estimated capacity values.',
      },
      {
        use: 'construction-peak-workers-per-mw',
        status: 'confirmed-with-limitation',
        conclusion: 'The source supports peak direct construction workers per total utility MW; project reporting conventions vary.',
      },
    ],
    limitations: [
      'Some source-project MW values are separately reported or analyst-estimated.',
      'The report warns that announcements can mix core onsite roles with broader support roles.',
    ],
  },
  {
    sourceId: 'uptime-data-center-staffing-2015',
    sourceFingerprint: 'a543dae4fc9e773cac4f89174489b16a4619ec5e6a321a82716c14f7d68d6b79',
    policyId: ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID,
    assessedAt: '2026-08-27',
    assessedBy: 'codex-assisted-release-qa',
    method: 'source-context-review',
    disposition: 'release-eligible',
    excerpt: {
      status: 'confirmed',
      conclusion: 'The stored minimum of one to two qualified operators onsite continuously is a verbatim substring of the recommendation.',
      locator: 'Staffing 24x7 section',
    },
    denominator: {
      status: 'not-applicable',
      conclusion: 'The recommendation is a continuously staffed-position floor and is not expressed per MW.',
      locator: 'Staffing 24x7 section',
    },
    workforceDefinition: {
      status: 'confirmed',
      conclusion: 'The recommendation concerns qualified onsite operators providing facility shift presence; it is not total facility employment.',
      locator: 'Staffing and Staffing 24x7 sections',
    },
    projectPhase: {
      status: 'confirmed',
      conclusion: 'The recommendation applies to operations at facilities whose critical business objectives require Tier III or IV availability.',
      locator: 'Staffing 24x7 section',
    },
    vintage: {
      status: 'confirmed-with-limitation',
      conclusion: 'The 2015 source remains suitable as a critical-operations coverage principle, not as a current typical total-headcount benchmark.',
      locator: 'Publication date and article scope',
    },
    claims: [
      {
        use: 'operations-coverage-floor',
        status: 'confirmed-with-limitation',
        conclusion: 'Supports one to two qualified onsite operator positions continuously; the conversion to 4.2-8.4 FTE excludes leave, backfill, and all other roles.',
      },
    ],
    limitations: [
      'The source is from 2015 and does not measure contemporary total staffing.',
      'IT, security, management, janitorial, contractor, leave, and backfill staffing are outside the coefficient.',
    ],
  },
  {
    sourceId: 'kern-edc-rb-inyokern-cec-2026',
    sourceFingerprint: '815b496418161afa941b31274ef352ba2a48f19419e90c2a08dc38b4d4c78f3b',
    policyId: ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID,
    assessedAt: '2026-08-27',
    assessedBy: 'codex-assisted-release-qa',
    method: 'source-context-review',
    disposition: 'release-eligible',
    artifactSha256: '86ec5d9deaf80117d7c88dd549fb052dd678c1f65dd6ffb99a8c3ed730346e6d',
    excerpt: {
      status: 'confirmed',
      conclusion: 'The letter reports a proposed 99 MW campus, more than 590 peak construction workers, and 30-60 permanent jobs.',
      locator: 'Letter body, unnumbered p. 1 (PDF p. 3)',
    },
    denominator: {
      status: 'confirmed-with-limitation',
      conclusion: 'The letter alone does not define MW basis; the applicant docket appendix identifies about 70 MW IT load and about 99 MW total electrical demand confirmed for SCE interconnection.',
      locator: 'Companion Appendix O, Environmental Sustainability Plan, p. 32 of 72',
    },
    workforceDefinition: {
      status: 'confirmed-with-limitation',
      conclusion: 'Peak construction and permanent project jobs are explicit, but the letter does not separately define direct, onsite, contractor, or tenant scope.',
      locator: 'Letter body, unnumbered p. 1 (PDF p. 3)',
    },
    projectPhase: {
      status: 'confirmed',
      conclusion: 'All quantities describe the proposed full 99 MW campus upon completion; they are not observed operating outcomes.',
      locator: 'Letter body and companion Appendix O project-parameter table',
    },
    vintage: {
      status: 'confirmed',
      conclusion: 'The May 2026 letter and April 2026 application context describe the same pending CEC docket.',
      locator: 'Docket 26-SPPE-01',
    },
    claims: [
      {
        use: 'operations-fte-per-mw',
        status: 'confirmed-with-limitation',
        conclusion: 'Supports a 30-60 permanent-jobs cross-check against 99 MW total electrical demand; direct/onsite scope is not separately defined.',
      },
      {
        use: 'construction-peak-workers-per-mw',
        status: 'confirmed-with-limitation',
        conclusion: 'Supports a greater-than-590 peak-worker cross-check against the proposed 99 MW full campus; the model conservatively uses 590.',
      },
      {
        use: 'operations-coverage-floor',
        status: 'confirmed-with-limitation',
        conclusion: 'The disclosed permanent workforce is above the qualified-operator coverage floor, providing scale consistency rather than an independent derivation of that floor.',
      },
    ],
    supportingContext: [
      {
        title: 'RB Inyokern Data Center - SPPE Application - Appendix O',
        publisher: 'R&L Capital, Inc. filing in the California Energy Commission docket',
        url: 'https://efiling.energy.ca.gov/GetDocument.aspx?DocumentContentId=106775&tn=269666',
        excerpt: 'IT Load: ~70 MW aggregate. Total Electrical Demand: ~99 MW (confirmed SCE interconnection per MOS174).',
        locator: 'Environmental Sustainability Plan, project-parameter table, p. 32 of 72',
      },
    ],
    limitations: [
      'The values are proposed or announced, not verified actual employment.',
      'The letter does not independently define direct or onsite job scope.',
    ],
  },
  {
    sourceId: 'terawulf-kentucky-psc-2026',
    sourceFingerprint: '0541f241565e3baeb778cc5bede5865a3898b94bef3afd59dd8365d94e8c5f13',
    policyId: ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID,
    assessedAt: '2026-08-27',
    assessedBy: 'codex-assisted-release-qa',
    method: 'source-context-review',
    disposition: 'release-eligible',
    artifactSha256: '1e8d76be16f622c3926e48e99908a5ada7f47d5b46e49cc9cb64bc62be35c853',
    excerpt: {
      status: 'confirmed',
      conclusion: 'The filing reports 482 MW, 800-1,000 peak construction jobs, and 80-100 permanent data-center jobs.',
      locator: 'Motion, pp. 2 and 4',
    },
    denominator: {
      status: 'confirmed',
      conclusion: 'The filing identifies 482 MW of transmission capacity and service and a ramp to full 482 MW data-center load.',
      locator: 'Motion, pp. 2-4',
    },
    workforceDefinition: {
      status: 'confirmed-with-limitation',
      conclusion: 'Peak construction and permanent data-center jobs are explicit, but direct, onsite, contractor, and tenant boundaries are not separately defined.',
      locator: 'Motion, p. 4',
    },
    projectPhase: {
      status: 'confirmed',
      conclusion: 'The values describe the proposed project at full 482 MW load by the end of 2027, contingent on approval and financing.',
      locator: 'Motion, p. 4',
    },
    vintage: {
      status: 'confirmed',
      conclusion: 'The May 2026 filing describes a current proposed load ramp and projected employment.',
      locator: 'Motion date and requested schedule, pp. 4-6',
    },
    claims: [
      {
        use: 'operations-fte-per-mw',
        status: 'confirmed-with-limitation',
        conclusion: 'Supports an 80-100 permanent-jobs cross-check against 482 MW utility service; direct/onsite scope is not separately defined.',
      },
      {
        use: 'construction-peak-workers-per-mw',
        status: 'confirmed-with-limitation',
        conclusion: 'Supports an 800-1,000 peak construction-jobs cross-check against 482 MW full utility load.',
      },
      {
        use: 'operations-coverage-floor',
        status: 'confirmed-with-limitation',
        conclusion: 'The disclosed permanent workforce is above the qualified-operator coverage floor, providing scale consistency rather than an independent derivation of that floor.',
      },
    ],
    limitations: [
      'The figures are applicant projections in a motion, not verified actual outcomes.',
      'The filing does not separately define direct or onsite job scope.',
    ],
  },
  {
    sourceId: 'amazon-gilroy-cec-sppe-2021',
    sourceFingerprint: 'f2bd9d5fade79d8bcf8cffcc64055dfd3ccffda85321a30bd8591156a30c8003',
    policyId: ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID,
    assessedAt: '2026-08-27',
    assessedBy: 'codex-assisted-release-qa',
    method: 'source-context-review',
    disposition: 'release-eligible',
    artifactSha256: 'c2d7c55a26711d263e98ef6f9c66069ab046a629fe173ec3a8a83969cb5e0e1f',
    excerpt: {
      status: 'confirmed',
      conclusion: 'The revised project description supports the stored capacity, construction duration, workforce, and operations quantities.',
      locator: 'Project description, pp. 12, 23, and 26',
    },
    denominator: {
      status: 'confirmed',
      conclusion: 'Approximately 96 MW is full-buildout facility electrical demand including critical IT and non-IT load; the document separately reports IT load and PUE.',
      locator: 'Project description, pp. 11-12',
    },
    workforceDefinition: {
      status: 'confirmed-with-limitation',
      conclusion: 'The filing reports average and peak construction workforce for each phase and separately reports full-time operating employees and occasional contractors.',
      locator: 'Workforce, p. 26',
    },
    projectPhase: {
      status: 'confirmed-with-limitation',
      conclusion: 'The 21-month duration covers Phase I and Phase II and the 75/110 workforce applies to each phase; the CEC proceeding was later terminated after withdrawal.',
      locator: 'Site Water Supply and Use, p. 23; Workforce, p. 26; CEC project summary',
    },
    vintage: {
      status: 'confirmed-with-limitation',
      conclusion: 'The 2021 planned-project quantities remain usable as one broad construction-planning anchor, not as an observed outcome.',
      locator: 'Filing date and CEC project status',
    },
    claims: [
      {
        use: 'construction-average-to-peak-ratio',
        status: 'confirmed-with-limitation',
        conclusion: 'Supports 75 average / 110 peak for each planned phase; no completed-project outcome is reported.',
      },
      {
        use: 'construction-duration-years',
        status: 'confirmed-with-limitation',
        conclusion: 'Supports a 21-month Phase I and Phase II construction period; it is a planned schedule for a project later withdrawn.',
      },
    ],
    supportingContext: [
      {
        title: 'Gilroy Backup Generating Facility project page',
        publisher: 'California Energy Commission',
        url: 'https://www.energy.ca.gov/powerplant/reciprocating-engine/gilroy-backup-generating-facility',
        excerpt: 'Project Status: Terminated.',
        locator: 'Project summary',
      },
    ],
    limitations: [
      'The project was withdrawn and the proceeding terminated; quantities are planning estimates only.',
      'The 21-month elapsed period should not be treated as a site-specific schedule commitment.',
    ],
  },
  {
    sourceId: 'vantage-ca3-cec-final-eir-2022',
    sourceFingerprint: '7031a4745205ee59d17d7b6437201eaf5fa0305c1c19c9aeb789af7c2d362ded',
    policyId: ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID,
    assessedAt: '2026-08-27',
    assessedBy: 'codex-assisted-release-qa',
    method: 'source-context-review',
    disposition: 'release-eligible',
    artifactSha256: '158893efc27fe8aca3c2798ae5f989a08784a9ee91800cd0c3ce1fe213ca490c',
    excerpt: {
      status: 'confirmed',
      conclusion: 'The EIR reports Phase I and II duration and workforce values and the combined 90-average/175-peak estimate.',
      locator: 'Transportation section 4.17, p. 4.17-6',
    },
    denominator: {
      status: 'not-applicable',
      conclusion: 'The coefficient uses average-to-peak workforce and elapsed duration; MW is descriptive project context rather than the divisor.',
      locator: 'Transportation section 4.17, p. 4.17-6',
    },
    workforceDefinition: {
      status: 'confirmed',
      conclusion: 'The figures are explicitly construction workforce counts used for project transportation analysis.',
      locator: 'Transportation section 4.17, p. 4.17-6',
    },
    projectPhase: {
      status: 'confirmed-with-limitation',
      conclusion: 'The EIR gives 14 months for Phase I and 11 months for Phase II, with an elapsed January 2022-to-early-2025 project window that includes a gap between phases.',
      locator: 'Transportation section 4.17, p. 4.17-6',
    },
    vintage: {
      status: 'confirmed-with-limitation',
      conclusion: 'The 2022 EIR is suitable as a second public planning anchor; it is not a verified construction completion record.',
      locator: 'Final EIR publication and project schedule',
    },
    claims: [
      {
        use: 'construction-average-to-peak-ratio',
        status: 'confirmed',
        conclusion: 'Supports a combined 90 average / 175 peak construction-workforce ratio.',
      },
      {
        use: 'construction-duration-years',
        status: 'confirmed-with-limitation',
        conclusion: 'Supports an approximately three-year elapsed project window, including a period between the two active construction phases.',
      },
    ],
    limitations: [
      'The high duration bound is elapsed calendar time, not three years at peak or average staffing.',
      'The values are project estimates summarized by CEC staff, not verified payroll records.',
    ],
  },
];

const AUDIT_BY_SOURCE_ID = new Map(
  ECONOMIC_SOURCE_SEMANTIC_AUDITS.map((audit) => [audit.sourceId, audit]),
);

function semanticUseForEntry(entry: EconomicSourceCatalogEntry): EconomicSemanticUse {
  switch (entry.coefficient.kind) {
    case 'operations-coverage-floor':
      return 'operations-coverage-floor';
    case 'size-band-intensity':
      return entry.coefficient.use;
    case 'construction-average-to-peak-ratio':
      return 'construction-average-to-peak-ratio';
    case 'construction-duration-years':
      return 'construction-duration-years';
  }
}

function usableFinding(status: EconomicSemanticFindingStatus): boolean {
  return status === 'confirmed' || status === 'confirmed-with-limitation';
}

export function validateEconomicCatalogEntrySemanticAudit(
  entry: EconomicSourceCatalogEntry,
): string[] {
  const errors: string[] = [];
  const requiredUse = semanticUseForEntry(entry);
  let claimSupportCount = 0;
  for (const source of entry.sources) {
    const audit = AUDIT_BY_SOURCE_ID.get(source.id);
    if (!audit) {
      errors.push(`${source.id}: semantic audit record is required.`);
      continue;
    }
    if (audit.policyId !== ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID) {
      errors.push(`${source.id}: semantic audit policy version is not current.`);
    }
    if (audit.sourceFingerprint !== economicSourceSemanticFingerprint(source)) {
      errors.push(`${source.id}: catalog source changed after semantic audit.`);
    }
    if (!Number.isFinite(Date.parse(audit.assessedAt)) || !audit.assessedBy.trim()) {
      errors.push(`${source.id}: semantic audit identity and date are required.`);
    }
    if (audit.disposition !== 'release-eligible') {
      errors.push(`${source.id}: semantic audit disposition withholds release.`);
    }
    if (!usableFinding(audit.excerpt.status)) {
      errors.push(`${source.id}: excerpt meaning is not established.`);
    }
    if (!usableFinding(audit.workforceDefinition.status)) {
      errors.push(`${source.id}: workforce definition is not established.`);
    }
    if (!usableFinding(audit.projectPhase.status)) {
      errors.push(`${source.id}: project phase is not established.`);
    }
    if (!usableFinding(audit.vintage.status)) {
      errors.push(`${source.id}: vintage is not defensible for the permitted use.`);
    }
    if (
      (requiredUse === 'operations-fte-per-mw' ||
        requiredUse === 'construction-peak-workers-per-mw') &&
      !usableFinding(audit.denominator.status)
    ) {
      errors.push(`${source.id}: MW denominator is not established.`);
    }
    const claim = audit.claims.find((candidate) => candidate.use === requiredUse);
    if (claim && usableFinding(claim.status)) claimSupportCount += 1;
    for (const check of source.normalizedChecks ?? []) {
      const normalizedClaim = audit.claims.find(
        (candidate) => candidate.use === check.use,
      );
      if (!normalizedClaim || !usableFinding(normalizedClaim.status)) {
        errors.push(`${source.id}: normalized ${check.use} claim is not semantically established.`);
      }
    }
    if (audit.artifactSha256 && !/^[a-f0-9]{64}$/.test(audit.artifactSha256)) {
      errors.push(`${source.id}: artifact SHA-256 is invalid.`);
    }
  }
  if (claimSupportCount < 2) {
    errors.push(`${entry.id}: semantic audit requires two source records supporting ${requiredUse}.`);
  }
  return [...new Set(errors)];
}

export interface EconomicSourceSemanticAuditReport {
  ready: boolean;
  policyId: typeof ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID;
  sourceCount: number;
  blockers: readonly string[];
  sources: readonly {
    sourceId: string;
    disposition: EconomicSourceSemanticAuditRecord['disposition'] | 'missing';
    assessedAt?: string;
    limitations: readonly string[];
  }[];
}

export function economicSourceSemanticAuditReport(
  catalog: EconomicSourceCatalogSnapshot,
): EconomicSourceSemanticAuditReport {
  const uniqueSources = new Map<string, EconomicSourceRef>();
  const blockers: string[] = [];
  for (const entry of catalog.entries) {
    for (const source of entry.sources) uniqueSources.set(source.id, source);
    blockers.push(
      ...validateEconomicCatalogEntrySemanticAudit(entry).map(
        (error) => `${entry.id}: ${error}`,
      ),
    );
  }
  return {
    ready: blockers.length === 0,
    policyId: ECONOMIC_SOURCE_SEMANTIC_AUDIT_POLICY_ID,
    sourceCount: uniqueSources.size,
    blockers: [...new Set(blockers)],
    sources: [...uniqueSources.values()].map((source) => {
      const audit = AUDIT_BY_SOURCE_ID.get(source.id);
      return {
        sourceId: source.id,
        disposition: audit?.disposition ?? 'missing',
        assessedAt: audit?.assessedAt,
        limitations: audit?.limitations ?? [],
      };
    }),
  };
}
